import React, { useState, useEffect } from "react";
import {
  Form, Input, Button, Typography, Alert, Spin, Tag, message,
} from "antd";
import {
  UserOutlined, PhoneOutlined, MailOutlined, CalendarOutlined,
  ClockCircleOutlined, TeamOutlined, CheckCircleOutlined,
  MinusOutlined, PlusOutlined, ArrowLeftOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getPublicVenueEvents, getPublicVenueEventById, bookPublicEvent, type PublicVenueEvent } from "../../utils/api/venueEventsAPI";
import "../PublicReservation/PublicReservation.scss";
import "./PublicEventBooking.scss";

const { Text } = Typography;

const EVENT_TYPE_LABELS: Record<string, string> = {
  concert: "Concert", standup: "Stand-up", band: "Live Band",
  private: "Private Event", sports: "Sports", other: "Other",
};

type Step = "list" | "book" | "success";

const PublicEventBooking: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("clientId") || "";
  const venueName = params.get("name") || "Venue";
  const singleEventId = params.get("eventId") || null;

  const [events, setEvents] = useState<PublicVenueEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>(singleEventId ? "book" : "list");
  const [selectedEvent, setSelectedEvent] = useState<PublicVenueEvent | null>(null);
  const [ticketsCount, setTicketsCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmData, setConfirmData] = useState<{ event: PublicVenueEvent; tickets: number } | null>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    if (singleEventId) {
      // Per-event URL — load only that specific event
      setLoadingEvents(true);
      getPublicVenueEventById(singleEventId)
        .then((res) => {
          setSelectedEvent(res.data);
          setStep("book");
        })
        .catch(() => setEventsError("Could not load event. The link may be invalid or the event is no longer available."))
        .finally(() => setLoadingEvents(false));
    } else if (clientId) {
      // Global URL — load all events
      setLoadingEvents(true);
      getPublicVenueEvents(clientId)
        .then((res) => setEvents(res.data))
        .catch(() => setEventsError("Could not load events. Please try again."))
        .finally(() => setLoadingEvents(false));
    }
  }, [clientId, singleEventId]);

  const handleSelectEvent = (event: PublicVenueEvent) => {
    setSelectedEvent(event);
    setTicketsCount(1);
    form.resetFields();
    setStep("book");
  };

  const handleSubmit = async (values: any) => {
    if (!selectedEvent) return;
    setSubmitting(true);
    try {
      await bookPublicEvent(selectedEvent._id, {
        guestName: values.guestName.trim(),
        guestPhone: values.guestPhone.trim(),
        guestEmail: values.guestEmail?.trim() || undefined,
        ticketsCount,
        note: values.note?.trim() || undefined,
      });
      setConfirmData({ event: selectedEvent, tickets: ticketsCount });
      setStep("success");
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId && !singleEventId) {
    return (
      <div className="pr-wrap pr-wrap--error">
        <Alert type="error" message="Invalid booking link. Please contact the venue." />
      </div>
    );
  }

  if (singleEventId && loadingEvents) {
    return (
      <div className="pr-wrap">
        <div className="pr-loading" style={{ minHeight: "60vh", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (singleEventId && eventsError) {
    return (
      <div className="pr-wrap pr-wrap--error">
        <Alert type="error" message={eventsError} showIcon />
      </div>
    );
  }

  return (
    <div className="pr-wrap peb-wrap">
      {/* Header */}
      <div className="pr-header">
        <div className="pr-header__emoji">🎭</div>
        <h1 className="pr-header__name">{venueName}</h1>
        <p className="pr-header__sub">Event tickets & reservations</p>
      </div>

      {/* Step: event list */}
      {step === "list" && (
        <div className="pr-body">
          {loadingEvents && (
            <div className="pr-loading"><Spin size="small" /><span>Loading events...</span></div>
          )}
          {eventsError && <Alert type="warning" message={eventsError} showIcon className="pr-alert" />}
          {!loadingEvents && !eventsError && events.length === 0 && (
            <Alert type="info" message="No upcoming events at this time. Check back later!" showIcon className="pr-alert" />
          )}
          {!loadingEvents && events.map((event) => {
            const isSoldOut = event.available <= 0;
            const fillPct = Math.round(((event.totalCapacity - event.available) / event.totalCapacity) * 100);
            const isAlmostFull = fillPct >= 80;
            return (
              <div
                key={event._id}
                className={`peb-event-card${isSoldOut ? " peb-event-card--sold-out" : ""}`}
                onClick={() => !isSoldOut && handleSelectEvent(event)}
              >
                <div className="peb-event-card__header">
                  <div>
                    <div className="peb-event-card__title">{event.title}</div>
                    <div className="peb-event-card__type">{EVENT_TYPE_LABELS[event.type] || event.type}</div>
                  </div>
                  {isSoldOut
                    ? <Tag color="red">Sold Out</Tag>
                    : isAlmostFull
                      ? <Tag color="orange">Only {event.available} left</Tag>
                      : null
                  }
                </div>
                <div className="peb-event-card__meta">
                  <span><CalendarOutlined /> {dayjs(event.date).format("ddd, DD MMM YYYY")}</span>
                  <span><ClockCircleOutlined /> {event.time}{event.endTime ? ` – ${event.endTime}` : ""}</span>
                  {event.ticketPrice > 0
                    ? <span><strong>{event.ticketPrice} {event.currency}</strong> per ticket</span>
                    : <span>Free entry</span>
                  }
                </div>
                {event.description && <p className="peb-event-card__desc">{event.description}</p>}
                {!isSoldOut && <div className="peb-event-card__cta">Book Tickets →</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Step: booking form */}
      {step === "book" && selectedEvent && (
        <div className="pr-body">
          {!singleEventId && (
            <button className="peb-back" onClick={() => setStep("list")}>
              <ArrowLeftOutlined /> Back to events
            </button>
          )}

          {/* Event summary */}
          <div className="peb-selected-event">
            <div className="peb-selected-event__title">{selectedEvent.title}</div>
            <div className="peb-selected-event__meta">
              <span><CalendarOutlined /> {dayjs(selectedEvent.date).format("ddd, DD MMM YYYY")}</span>
              <span><ClockCircleOutlined /> {selectedEvent.time}</span>
              {selectedEvent.ticketPrice > 0 && (
                <span>{selectedEvent.ticketPrice} {selectedEvent.currency} / ticket</span>
              )}
            </div>
          </div>

          {/* Ticket count */}
          <div className="pr-field-block">
            <label className="pr-label"><TeamOutlined className="pr-label__icon" /> Number of Tickets</label>
            <div className="pr-stepper">
              <button type="button" className="pr-stepper__btn" onClick={() => setTicketsCount((n) => Math.max(1, n - 1))} disabled={ticketsCount <= 1}>
                <MinusOutlined />
              </button>
              <span className="pr-stepper__value">{ticketsCount}</span>
              <button type="button" className="pr-stepper__btn" onClick={() => setTicketsCount((n) => Math.min(selectedEvent.available, n + 1))} disabled={ticketsCount >= selectedEvent.available}>
                <PlusOutlined />
              </button>
            </div>
            {selectedEvent.ticketPrice > 0 && (
              <Text type="secondary" style={{ fontSize: 14 }}>
                Total: <strong>{(selectedEvent.ticketPrice * ticketsCount).toFixed(2)} {selectedEvent.currency}</strong>
              </Text>
            )}
          </div>

          <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false} className="pr-form">
            <Form.Item name="guestName" label="Full Name" rules={[{ required: true, message: "Please enter your name" }]}>
              <Input prefix={<UserOutlined />} placeholder="John Smith" size="large" className="pr-input" autoComplete="name" />
            </Form.Item>
            <Form.Item name="guestPhone" label="Phone Number" rules={[{ required: true, message: "Please enter your phone number" }]}>
              <Input prefix={<PhoneOutlined />} placeholder="+387 61 123 456" size="large" className="pr-input" type="tel" autoComplete="tel" inputMode="tel" />
            </Form.Item>
            <Form.Item name="guestEmail" label="Email (optional)" rules={[{ type: "email", message: "Invalid email address" }]}>
              <Input prefix={<MailOutlined />} placeholder="email@example.com" size="large" className="pr-input" type="email" autoComplete="email" inputMode="email" />
            </Form.Item>
            <Form.Item name="note" label="Note (optional)">
              <Input.TextArea rows={2} placeholder="Any special requests..." maxLength={300} className="pr-textarea" />
            </Form.Item>

            <Button type="primary" size="large" block htmlType="submit" loading={submitting} className="pr-cta" icon={<CheckCircleOutlined />}>
              Confirm Booking
            </Button>
          </Form>
        </div>
      )}

      {/* Step: success */}
      {step === "success" && confirmData && (
        <div className="pr-success">
          <div className="pr-success__icon"><CheckCircleOutlined /></div>
          <h2 className="pr-success__title">Booking Received!</h2>
          <p className="pr-success__msg">Your spot has been reserved. Please visit the venue to pick up your ticket — a staff member will confirm your booking upon arrival.</p>

          <div className="pr-success__details">
            <div className="pr-success__detail-row">
              <CalendarOutlined />
              <span>{dayjs(confirmData.event.date).format("dddd, DD MMMM YYYY")}</span>
            </div>
            <div className="pr-success__detail-row">
              <ClockCircleOutlined />
              <span>{confirmData.event.time}</span>
            </div>
            <div className="pr-success__detail-row">
              <TeamOutlined />
              <span>{confirmData.tickets} {confirmData.tickets === 1 ? "ticket" : "tickets"}</span>
            </div>
            {confirmData.event.ticketPrice > 0 && (
              <div className="pr-success__detail-row">
                <span>Amount due: <strong>{(confirmData.event.ticketPrice * confirmData.tickets).toFixed(2)} {confirmData.event.currency}</strong></span>
              </div>
            )}
          </div>

          {!singleEventId && (
            <Button size="large" block onClick={() => { setStep("list"); setSelectedEvent(null); }} className="pr-new-btn">
              View Other Events
            </Button>
          )}
        </div>
      )}

      <div className="pr-footer">
        <span>Powered by</span>
        <strong> FixmanHR</strong>
      </div>
    </div>
  );
};

export default PublicEventBooking;
